import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type BlueskySettings, type BlueskyTestResult } from '@/api/client'
import { qk, useBlueskySettings } from '@/lib/queries'
import { TONE_CLASS } from '@/lib/status'
import { cn } from '@/lib/utils'
import { Button, ConfirmDialog, ErrorState, Field, Input, Skeleton, errorText } from '@/ui'

/** One line for a test result, the way the old panel wrote it. */
export function bskyResultText(r: BlueskyTestResult): string {
  return r.ok ? `@${r.handle} (${r.did}) 로그인 성공 · ${r.latency_ms ?? '?'}ms` : (r.error ?? '실패')
}

/** Logs in to Bluesky with the stored app password. A real call: only a
 *  person presses it. */
export function useTestBluesky() {
  return useMutation({ mutationFn: () => api.testBluesky() })
}

export function useSaveBluesky() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (p: Parameters<typeof api.saveBlueskySettings>[0]) => api.saveBlueskySettings(p),
    onSuccess: (s) => qc.setQueryData(qk.bluesky, s),
  })
}

export function TestResultLine({ result, error }: { result?: BlueskyTestResult | null; error?: unknown }) {
  if (!result && !error) return null
  const ok = !!result?.ok
  return (
    <div
      role="status"
      className={cn(
        'rounded-md border px-3 py-2 text-meta',
        ok ? TONE_CLASS.published.border : TONE_CLASS.failed.border,
        ok ? TONE_CLASS.published.soft : TONE_CLASS.failed.soft,
      )}
    >
      {result ? `${ok ? '✓' : '✗'} ${bskyResultText(result)}` : `✗ ${errorText(error)}`}
    </div>
  )
}

/** The Bluesky login of the redeem desk: handle, PDS and the app password.
 *  The password is write-only — the server answers "set" or "not set" and
 *  never the value, so the field is always empty on load and leaving it
 *  empty keeps what is stored. Used on the accounts page and as the
 *  /settings?tab=bluesky panel (the manifest names it, so both stay). */
export default function BlueskyPanel({ onDirtyChange }: { onDirtyChange?: (dirty: boolean) => void }) {
  const q = useBlueskySettings()
  const saved = q.data
  const [handle, setHandle] = useState('')
  const [pds, setPds] = useState('')
  const [password, setPassword] = useState('')
  const [clearing, setClearing] = useState(false)
  const save = useSaveBluesky()
  const test = useTestBluesky()

  const reset = (s: BlueskySettings) => {
    setHandle(s.handle)
    setPds(s.pds_url)
    setPassword('')
  }
  useEffect(() => {
    if (saved) reset(saved)
  }, [saved])

  const dirty = !!saved && (handle !== saved.handle || pds !== saved.pds_url || password !== '')
  useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange])

  if (!saved) {
    return q.error ? (
      <ErrorState title="Bluesky 설정을 불러오지 못했다" error={q.error} onRetry={() => q.refetch()} />
    ) : (
      <Skeleton className="h-64" />
    )
  }

  const failed = save.error ?? undefined

  return (
    <section className="space-y-4 rounded-lg border border-border bg-card p-4 sm:p-5">
      <div>
        <h3 className="text-section font-semibold">Bluesky 데스크 계정</h3>
        <p className="mt-1 text-table text-muted-foreground">
          리딤 데스크가 Bluesky 홍보글을 올리고, 이 계정으로 온 DM을 읽어 같은 대화에 코드를 보낸다. 앱 비밀번호는
          Bluesky 설정 → 개인정보 및 보안 → 앱 비밀번호에서 만들고, <b>“다이렉트 메시지 접근 허용”</b>을 켜야 한다.
          모르는 사람의 DM을 받으려면 채팅 설정에서 “누구나”로 열어 둔다.
        </p>
      </div>

      <Field label="핸들">
        <Input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="pentarab.bsky.social" autoComplete="off" />
      </Field>

      <Field
        label="앱 비밀번호"
        aside={
          <span className={saved.password_set ? TONE_CLASS.published.text : TONE_CLASS.failed.text}>
            {saved.password_set ? '설정됨' : '미설정'}
          </span>
        }
        error={!saved.can_store ? '서버에 ENCRYPTION_KEY가 없어 비밀번호를 저장할 수 없다.' : undefined}
      >
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={saved.password_set ? '비워 두면 저장된 값 유지' : 'xxxx-xxxx-xxxx-xxxx'}
          autoComplete="new-password"
        />
      </Field>

      <Field label="PDS 주소" hint="bsky.social 계정이면 그대로 둔다. 자체 PDS일 때만 바꾼다.">
        <Input value={pds} onChange={(e) => setPds(e.target.value)} placeholder="https://bsky.social" />
      </Field>

      {failed != null && <ErrorState title="저장하지 못했다" error={failed} />}
      <TestResultLine result={test.data} error={test.error} />

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          loading={save.isPending}
          disabled={!dirty}
          onClick={() => save.mutate({ handle, pds_url: pds, app_password: password })}
        >
          저장
        </Button>
        <Button
          loading={test.isPending}
          disabled={dirty || !saved.password_set}
          title={dirty ? '먼저 저장한다 — 테스트는 저장된 값으로 로그인한다' : undefined}
          onClick={() => test.mutate()}
        >
          연결 테스트
        </Button>
        {saved.password_set && (
          <Button variant="ghost" className="ml-auto text-status-failed" onClick={() => setClearing(true)}>
            비밀번호 삭제
          </Button>
        )}
      </div>

      <ConfirmDialog
        open={clearing}
        onOpenChange={setClearing}
        title="저장된 앱 비밀번호를 지울까"
        description="데스크가 Bluesky를 더 이상 읽지도 보내지도 않는다."
        confirmLabel="삭제"
        confirmVariant="destructive"
        onConfirm={async () => {
          await save.mutateAsync({ handle: saved.handle, pds_url: saved.pds_url, clear_password: true })
          test.reset()
          setClearing(false)
        }}
      />
    </section>
  )
}
